import { supabase } from './supabase';

// ─────────────────────────────────────────────────────────────────────────────
// Server-side conversation summaries for the Messages list.
// One small row per conversation (vs downloading ~500 full messages on open).
// Backed by the `get_message_conversations` RPC.
// ─────────────────────────────────────────────────────────────────────────────

export interface ConversationSummary {
  conversation_key: string;
  conversation_type: 'customer' | 'phone' | 'email';
  customer_id: string | null;
  customer_name: string | null;
  is_supplier: boolean;
  contact_info: string | null;
  last_message_at: string | null;
  last_preview: string | null;
  /** Email subject of the last message (null for SMS/calls or pre-upgrade RPC). */
  last_subject?: string | null;
  last_message_type: 'sms' | 'email' | 'call' | string | null;
  last_direction: 'inbound' | 'outbound' | null;
  unread_count: number;
  has_unread: boolean;
}

/**
 * Fetch a page of conversation summaries for a business, ordered by most recent
 * activity. Pagination via limit/offset. Returns [] on error (callers can fall
 * back to the legacy client-side build).
 */
export async function getMessageConversations(
  businessId: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<ConversationSummary[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('get_message_conversations', {
    p_business_id: businessId,
    p_limit: opts.limit ?? 30,
    p_offset: opts.offset ?? 0,
  });
  if (error) {
    console.error('[getMessageConversations] RPC error:', error);
    throw error;
  }
  return (data || []) as ConversationSummary[];
}

/**
 * Conversations that still have unread — including threads too old to appear
 * in the recency-capped inbox list. Returns [] on error so callers can keep
 * the existing 200-recent list.
 */
export async function getUnreadMessageConversations(
  businessId: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<ConversationSummary[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('get_unread_message_conversations', {
    p_business_id: businessId,
    p_limit: opts.limit ?? 500,
    p_offset: opts.offset ?? 0,
  });
  if (error) {
    console.warn('[getUnreadMessageConversations] RPC error (non-fatal):', error);
    return [];
  }
  return (data || []) as ConversationSummary[];
}

/** Recent list first; append unread threads that the recency cap dropped.
 *  When the same conversation appears in both, keep the row with unread
 *  (and the newer preview) so a stale recent row can't hide SMS unread. */
export function mergeRecentAndUnreadConversations(
  recent: ConversationSummary[],
  unread: ConversationSummary[],
): ConversationSummary[] {
  const byKey = new Map<string, ConversationSummary>();
  for (const s of [...recent, ...unread]) {
    const key = s.conversation_key || '';
    if (!key) continue;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, s);
      continue;
    }
    const existingTime = new Date(existing.last_message_at || 0).getTime();
    const nextTime = new Date(s.last_message_at || 0).getTime();
    const preferNext =
      (!!s.has_unread && !existing.has_unread) ||
      (s.has_unread === existing.has_unread && nextTime > existingTime) ||
      (Number(s.unread_count) || 0) > (Number(existing.unread_count) || 0);
    if (preferNext) byKey.set(key, s);
  }
  return Array.from(byKey.values());
}
